import moment from "moment";
import { cloneDeep } from "lodash";
import {
    AGE_LIMIT_LAMBDA,
    IW_PRIZE_LIMITS_LAMBDA,
    PARTICIPATION_LIMIT_LAMBDA,
    limitsStateValues,
    participationLimitsProps,
    limits
} from "../../../../types/componentTypes/limits";
import { ConfigType } from "../../../../types/configuration";
import { limitChekerLambdas } from '../constants/limit-constants';
import { PARTICIPATION_LIMIT_SCHEMA, WINNING_LIMIT_SCHEMA } from "./limit-schemas";

/**
 * Deletes a checkerLambdas property if its value is an empty array
 * @param flowConfig { checkerLambdas?: string[] }
 */
export const removeCheckerLambdasWhenEmpty = (flowConfig: { checkerLambdas?: string[] }) => {
    if (flowConfig?.checkerLambdas && flowConfig.checkerLambdas.length === 0) {
        delete flowConfig.checkerLambdas;
    }
};

export const getIWLimitsForConfig = (config: limitsStateValues | ConfigType) => {
    const {
        instantWinLimitPerTimeInterval,
        instantWinLimitPerConfiguration,
        instantWinLimitPerTimeIntervalValue,
    } =
        'tempParams' in config ? config.tempParams : config.flow.instantWin?.params;

    return {
        ...(instantWinLimitPerConfiguration
            ? { instantWinLimitPerConfiguration }
            : {}),
        ...(instantWinLimitPerTimeInterval
            ? { instantWinLimitPerTimeInterval }
            : {}),
        ...(instantWinLimitPerTimeIntervalValue
            ? { instantWinLimitPerTimeIntervalValue }
            : {}),
    };
};

export const reducePartLimitValues = (inputObj: participationLimitsProps) => {
    return (Object.keys(inputObj).flatMap(key => {
        if (typeof (inputObj[key]) === 'object') {
            return Object.values(inputObj[key])
        }
        if (typeof (inputObj[key]) === 'number') {
            return inputObj[key]
        }
        return undefined
    })).filter(value => value).reduce(
        (accum, currentValue) => accum + currentValue
    );
}

export const shouldDeleteBlockEntry = (initialObj: participationLimitsProps, updatedObj: participationLimitsProps, initialLimits: limits, updatedLimits: limits) => {
    if (JSON.stringify(Object.values(initialLimits)) !== JSON.stringify(Object.values(updatedLimits))) {
        return true
    }
    return reducePartLimitValues(initialObj) < reducePartLimitValues(updatedObj) ? true : false;
}

export const validateCheckerLambdas = (config: ConfigType | limitsStateValues, flowName: string) => {
    const checkerLambdas = config.flow[flowName]?.checkerLambdas || [];
    const { params } = config.flow[flowName]

    if (!params) return [];

    const {
        instantWinLimitPerTimeIntervalValue,
        instantWinLimitPerTimeInterval,
        instantWinLimitPerConfiguration,
        instantWinCalendarCycleLimit,
    } = params;

    if (instantWinLimitPerTimeIntervalValue || instantWinLimitPerTimeInterval || instantWinLimitPerConfiguration || instantWinCalendarCycleLimit)
        return [...checkerLambdas, IW_PRIZE_LIMITS_LAMBDA];

    return checkerLambdas;
};

export const convertDatesToTimestamp = (values: limitsStateValues, dateFields: Array<string>) => {
    dateFields.forEach((field) => {
        const date =
            values.participationLimits.participationLimitStartEndDatesRange?.[
            field
            ];
        const calendarDate =
            values.participationLimits.participationLimitCalendarDatesRange?.[
            field
            ];
        if (date && !Number.isInteger(date)) {
            values.participationLimits.participationLimitStartEndDatesRange[field] =
                moment(date).valueOf();
        }

        if (calendarDate && !Number.isInteger(calendarDate)) {
            values.participationLimits.participationLimitCalendarDatesRange[field] =
                moment(calendarDate).valueOf();
        }
    });
    return values;
};

export const deleteKeys = <T extends object>(obj: T | undefined, keys: readonly (keyof T | string)[]) => {
    if (!obj) return;
    keys.forEach((key) => {
        if (!(key in (obj as any))) {
            return;
        }
        delete (obj as any)[key];
    });
};

export const clearParticipationLimits = (
    configValues: limitsStateValues,
    flowName: string
) => {
    const values = JSON.parse(JSON.stringify(configValues));

    const limitStates = values.tempParams.limits;
    const participationLimits = values.participationLimits;
    const flow = values.flow?.[flowName];
    const formParams = flow?.params;

    if (!flow) return values;

    PARTICIPATION_LIMIT_SCHEMA.forEach(({ flag, keys }) => {
        if (!limitStates[flag]) {
            deleteKeys(participationLimits, keys);
            deleteKeys(formParams, keys);
        }
    });

    const useDatesLimit = Object.entries(limitStates).some(
        ([key, value]) => key !== 'minAge' && Boolean(value)
    );

    if (useDatesLimit || formParams?.participationLimitMultipleChecks) {
        values.participationLimits.participationLimitMultipleChecks = true;
        values.flow[flowName].params.participationLimitMultipleChecks = true;
        return convertDatesToTimestamp(values, ['startDate', 'endDate']);
    }

    deleteKeys(participationLimits, ['participationLimitMultipleChecks']);
    deleteKeys(formParams, ['participationLimitMultipleChecks']);

    return values;
};

export const clearAlwaysWinLimits = (configValues: limitsStateValues, flowName: string) => {
    let values = cloneDeep(configValues) as limitsStateValues;

    values.flow[flowName].params.ratioWinning = values.alwaysWinLimits?.ratioWinning;

    // remove winning ratios from the config as they are will be saved in the DB instead
    if (values.alwaysWinLimits.alwaysWinPrizeRatios) {
        delete values.alwaysWinLimits.alwaysWinPrizeRatios;
        delete values.flow[flowName]?.params['alwaysWinPrizeRatios'];
    }

    return values;
}

export const clearWinningLimits = (configValues: limitsStateValues, flowName: string) => {
    const values = cloneDeep(configValues) as limitsStateValues;
    const flow = values.flow[flowName];
    const limitStates = values.tempParams;
    const formParams = flow?.params;

    if (!flow) return values;

    WINNING_LIMIT_SCHEMA.forEach(({ flag, keys, customFormField }) => {
        if (!limitStates[flag]) {
            deleteKeys(limitStates, keys);
            deleteKeys(formParams, keys);

            if (customFormField) {
                deleteKeys(values, customFormField);
            }
        }
    });

    return values;
};

export const getCheckerLambdasFromTempParams = (updatedLimitsValues: limitsStateValues, checkerLambdasAlreadyPresent: string[]): string[] => {
    // Filter only limit checker lambdas
    const checkerLambdas = new Set<string>(
        checkerLambdasAlreadyPresent?.filter(
            (l) => !limitChekerLambdas.includes(l),
        ),
    );

    // check tempParams states for IW limits
    const winningLimitStates = updatedLimitsValues.tempParams;
    WINNING_LIMIT_SCHEMA.forEach(({ flag }) => {
        if (winningLimitStates[flag]) {
            checkerLambdas.add(IW_PRIZE_LIMITS_LAMBDA);
        }
    });

    // check tempParams.limits states for participation limits
    const participationLimitStates = updatedLimitsValues.tempParams.limits;
    PARTICIPATION_LIMIT_SCHEMA.forEach(({ flag }) => {
        if (participationLimitStates[flag]) {
            checkerLambdas.add(
                flag === 'minAge' ? AGE_LIMIT_LAMBDA : PARTICIPATION_LIMIT_LAMBDA
            );
        }
    });

    return [...checkerLambdas];
};
